import { createContext } from 'react'
import type { ZoneRulesTable } from '@/lib/time'
import type {
  FieldsOf,
  ReferenceErrorCode,
  ReferenceManifest,
  ReferenceRowMap,
  ReferenceTableName,
} from './types'

export type ReferenceStatus = 'loading' | 'ready' | 'error'

export interface ReferenceContextValue {
  status: ReferenceStatus
  /** Provider in use, shown in the settings header. */
  providerName: string
  manifest: ReferenceManifest | null
  countries: ReferenceRowMap['country'][]
  timezones: ReferenceRowMap['timezone'][]
  airports: ReferenceRowMap['airport'][]
  aircraft: ReferenceRowMap['aircraft'][]
  cityPairs: ReferenceRowMap['cityPair'][]
  cityPairCapability: ReferenceRowMap['cityPairCapability'][]
  countryCapability: ReferenceRowMap['countryCapability'][]
  airportCapability: ReferenceRowMap['airportCapability'][]
  /** Ad-hoc departures from the three baselines above. */
  capabilityException: ReferenceRowMap['capabilityException'][]
  /** Named sets of stations and of countries, usable wherever one is named. */
  airportGroups: ReferenceRowMap['airportGroup'][]
  countryGroups: ReferenceRowMap['countryGroup'][]
  zoneRules: ZoneRulesTable
  /** Rows differing from the shipped seed, per table. */
  pending: Record<ReferenceTableName, number>
  create<T extends ReferenceTableName>(table: T, fields: FieldsOf<T>): Promise<void>
  update<T extends ReferenceTableName>(
    table: T,
    id: string,
    fields: Partial<FieldsOf<T>>,
  ): Promise<void>
  remove(table: ReferenceTableName, id: string): Promise<void>
  /** Discard every local change. */
  reset(): Promise<void>
  reload(): Promise<void>
}

export const ReferenceContext = createContext<ReferenceContextValue | null>(null)

/** The UI owns the wording; the provider only raises codes. */
export const REFERENCE_ERROR_MESSAGE: Record<ReferenceErrorCode, string> = {
  not_found: 'Row no longer exists.',
  duplicate_key: 'Already in use.',
  invalid_value: 'Invalid value.',
  seed_unavailable: 'Reference data unavailable.',
  storage_full: 'Local storage full.',
  unknown: 'Something went wrong.',
}
